import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useEditor, useNode } from '@craftjs/core';

import { EDITOR_RESOLVERS } from '@common/constants';
import { TextField } from '@mui/material';
import SettingsWrapper from '@common/components/SettingsWrapper/SettingsWrapper';
import SettingsRow from '@common/components/SettingsWrapper/SettingsRow';
import SizeStyle from '@resolvers/tools/SizeStyle/SizeStyle';
import MarginStyle from '@resolvers/tools/MarginStyle/MarginStyle';
import ColorButtons from '@resolvers/tools/ColorButtons/ColorButtons';
import PaddingStyle from '@resolvers/tools/PaddingStyle/PaddingStyle';
import FontSizeStyle from '@resolvers/tools/FontSizeStyle/FontSizeStyle';
import TextWithPlaceholderInput from '@resolvers/tools/TextWithPlaceholderInput/TextWithPlaceholderInput';

const Button = ({
  btnText,
  href,
  marginTop,
  marginLeft,
  background,
  fontColor,
  height,
  paddingVertical,
  paddingHorizontal,
  fontSize,
  borderRadius,
  width,
  textAlign,
}) => {
  const { actions } = useEditor();
  const {
    connectors: { connect, drag },
    actions: { setCustom },
  } = useNode();

  const ref = useRef(null);
  const { t } = useTranslation();

  const preventHref = true;

  useEffect(() => {
    if (ref?.current && connect) {
      connect(drag(ref.current));
    }
  }, [connect]);

  useEffect(() => {
    actions.history.ignore(() => {
      setCustom((props) => {
        props.displayName = t('button');
      });
    });
  }, [setCustom, t]);

  const handleClick = (e) => {
    if (preventHref) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <a
      ref={ref}
      href={href}
      target='_blank'
      onClick={handleClick}
      style={{
        margin: `0px`,
        marginTop: `${marginTop}px`,
        marginLeft: `${marginLeft}px`,
        backgroundColor: background,
        color: fontColor,
        height: `${height}px`,
        lineHeight: `${height}px`,
        padding: `${paddingVertical}px ${paddingHorizontal}px `,
        fontSize: `${fontSize}px`,
        border: 'none',
        borderRadius: `${borderRadius}px`,
        width: `${width}px`,
        textAlign,
        textDecoration: 'none',
        display: 'inline-block',
        textAlign: 'center',
        verticalAlign: 'middle',
      }}
    >
      {btnText || t('click_me')}
    </a>
  );
};

const ButtonConfigPanel = () => {
  const { t } = useTranslation();
  const {
    actions: { setProp },
    btnText,
    href,
  } = useNode(({ data: { props } }) => ({
    size: props.size,
    fluid: props.fluid,
    btnText: props.btnText,
    href: props.href,
  }));

  const handleChange = (e) => {
    setProp((props) => {
      props[e.target.name] = e.target.value;
    }, 500);
  };

  const handleChangeButtonHref = (value) => {
    setProp((props) => {
      props.href = value;
    }, 500);
  };

  return (
    <SettingsWrapper>
      <SettingsRow>
        <TextField
          label={t('button_text')}
          name='btnText'
          value={btnText}
          onChange={handleChange}
          size='small'
          sx={{ maxWidth: '250px', height: '40px' }}
        />
      </SettingsRow>
      <SettingsRow sx={{ mb: 3 }}>
        <TextWithPlaceholderInput
          value={href}
          setValue={handleChangeButtonHref}
          label={t('button_href')}
          placeholder={t('href_placeholder')}
          helperText={t('href_helper_text')}
          spaceDisabled={true}
        />
      </SettingsRow>
      <SettingsRow>
        <FontSizeStyle />
      </SettingsRow>
      <SettingsRow>
        <SizeStyle propKey='height' label={t('height')} />
        <SizeStyle propKey='width' label={t('width')} max={300} />
      </SettingsRow>
      <SettingsRow>
        <ColorButtons initBackGround='#2185d0' initFontColor='#FFF' />
      </SettingsRow>
      <PaddingStyle showVH={true} />
      <MarginStyle sx={{ mb: 3 }} />
    </SettingsWrapper>
  );
};

export const ButtonDefaultProps = {
  btnText: '',
  href: '',
  fluid: false,
  margin: '0',
  background: '#2185d0',
  fontColor: '#FFF',
  height: '14',
  paddingVertical: '10',
  paddingHorizontal: '15',
  marginTop: '10',
  marginLeft: '0',
  fontSize: '14',
  border: 'none',
  borderRadius: '4',
  width: '100',
  textAlign: 'center',
  isDeletable: true,
};

Button.defaultProps = ButtonDefaultProps;

Button.propTypes = {
  variant: PropTypes.string,
  fontColor: PropTypes.string,
  btnText: PropTypes.string,
  fluid: PropTypes.bool,
};

Button.craft = {
  displayName: EDITOR_RESOLVERS.COMMON.BUTTON,
  name: EDITOR_RESOLVERS.COMMON.BUTTON,
  props: ButtonDefaultProps,
  related: {
    settings: ButtonConfigPanel,
  },
};

export default Button;
